import { z } from 'zod';
import { Money } from './money';

export const TableShape = z.enum(['RECT', 'CIRCLE']);
export type TableShape = z.infer<typeof TableShape>;

/**
 * Виды мебели и ориентиров на схеме. RECT/CIRCLE/TEXT — «сырая
 * геометрия» первых планов, они живут дальше как стены, колонны
 * и свободные подписи. Остальные — то, по чему официант узнаёт СВОЙ
 * зал: диван вдоль стены, сцена, барная стойка, вход, туалет, окно.
 * У осмысленных видов цвет задаёт дизайн-система, а не владелец:
 * план остаётся спокойным, какие бы цвета ни хранились в базе.
 */
export const HallShapeKind = z.enum([
  'RECT', 'CIRCLE', 'TEXT',
  'SOFA', 'STAGE', 'BAR', 'ENTRANCE', 'WC', 'WINDOW', 'PLANT',
]);
export type HallShapeKind = z.infer<typeof HallShapeKind>;

/** Стол на схеме. Координаты в клетках сетки; null — не поставлен на план. */
export const PlanTable = z.object({
  id: z.string(),
  name: z.string(),
  seats: z.number().int(),
  x: z.number().int().nullable(),
  y: z.number().int().nullable(),
  w: z.number().int(),
  h: z.number().int(),
  shape: TableShape,
});
export type PlanTable = z.infer<typeof PlanTable>;

export const PlanShape = z.object({
  id: z.string(),
  kind: HallShapeKind,
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
  text: z.string().nullable(),
  color: z.string(),
  rotate: z.number().int(),
});
export type PlanShape = z.infer<typeof PlanShape>;

export const HallPlan = z.object({
  hallId: z.string(),
  hallName: z.string(),
  tables: z.array(PlanTable),
  shapes: z.array(PlanShape),
});
export type HallPlan = z.infer<typeof HallPlan>;

export const HallPlanList = z.object({ halls: z.array(HallPlan) });
export type HallPlanList = z.infer<typeof HallPlanList>;

/** Сохранение плана целиком: так проще, чем следить за каждым сдвигом. */
export const SaveHallPlanRequest = z.object({
  hallId: z.string(),
  tables: z.array(z.object({
    id: z.string(),
    x: z.number().int().min(0).max(200).nullable(),
    y: z.number().int().min(0).max(200).nullable(),
    w: z.number().int().min(1).max(30),
    h: z.number().int().min(1).max(30),
    shape: TableShape,
  })).max(300),
  shapes: z.array(z.object({
    /** новые фигуры приходят без id */
    id: z.string().optional(),
    kind: HallShapeKind,
    x: z.number().int().min(0).max(200),
    y: z.number().int().min(0).max(200),
    w: z.number().int().min(1).max(200),
    h: z.number().int().min(1).max(200),
    text: z.string().max(40).nullable().optional(),
    color: z.string().regex(/^[0-9a-fA-F]{6}$/).default('d8dbe0'),
    rotate: z.number().int().min(-90).max(90).default(0),
  })).max(200),
});
export type SaveHallPlanRequest = z.infer<typeof SaveHallPlanRequest>;

/**
 * Живое состояние стола для кассы. Это и есть отличие нашей схемы
 * от чужой картинки: официант видит не просто расстановку, а зал.
 */
export const TableState = z.enum([
  /** никого */
  'FREE',
  /** открытый заказ */
  'BUSY',
  /** гость просил счёт */
  'BILL',
  /** гость зовёт официанта */
  'CALL',
  /** бронь начинается в ближайший час */
  'RESERVED',
]);
export type TableState = z.infer<typeof TableState>;

export const LiveTable = z.object({
  id: z.string(),
  state: TableState,
  /** сумма открытого заказа */
  total: Money.nullable(),
  /** сколько минут стол занят */
  busyMin: z.number().int().nullable(),
  /** имя официанта на этом столе */
  waiter: z.string().nullable(),
  /** мой стол: официант находит свои с одного взгляда */
  mine: z.boolean().default(false),
  /** счёт напечатан — стол ждёт денег, а не заказа */
  awaitingPay: z.boolean().default(false),
  /** во сколько бронь: «19:30» */
  reservedAt: z.string().nullable(),
  guestName: z.string().nullable(),
});
export type LiveTable = z.infer<typeof LiveTable>;

export const HallLive = z.object({
  tables: z.array(LiveTable),
  at: z.string(),
});
export type HallLive = z.infer<typeof HallLive>;
